import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { RootState } from "store/rootReducer";

import useProductionVolumesQuery, {
  ProductionVolumesFilter
} from "api/useProductionVolumesQuery";

import { FilterCountT } from "models/filter";

import {
  MonthlyFilterShape as Known,
  changePage,
  rememberMonthlyShape,
  useTable
} from "../context";
import { MAX_CELL_COUNT } from "./useDataQuery";

// Columns before the first response says how many the filter has (plant liquids add four).
const FALLBACK_COLUMN_COUNT = 10;
// An assumed longest history for a well. It only caps the cells a page can hold, and
// nothing enforces it: a longer history simply makes a page larger.
const MAX_MONTHS_PER_WELL = 600;
// The most wells the service takes in a page.
const SERVICE_MAX_PAGE_SIZE = 2000;

/** Wells per page at which the grid stays within the Data Table's cell limit. */
export const monthlyMaxPageSize = (columnCount = FALLBACK_COLUMN_COUNT) =>
  Math.min(
    SERVICE_MAX_PAGE_SIZE,
    Math.floor(MAX_CELL_COUNT / (columnCount * MAX_MONTHS_PER_WELL))
  );

const FALLBACK_PAGE_SIZE = monthlyMaxPageSize();

// A filter that fits one page is requested whole, at a size that does not move when the
// response reveals fewer columns, which would only buy a second request. A larger filter
// pages at the picked size, by default the most that fits. With nothing known yet the
// request is page 1 at the fallback maximum, which covers a one-page filter in one request.
const planRequest = (
  known: Known | undefined,
  userPageSize: Nullable<number>,
  storedPage: number
) => {
  const cap = monthlyMaxPageSize(known?.columnCount);
  if (known === undefined) return { size: FALLBACK_PAGE_SIZE, currentPage: storedPage };
  if (known.totalWells <= cap) {
    return {
      size: known.totalWells > FALLBACK_PAGE_SIZE ? cap : FALLBACK_PAGE_SIZE,
      currentPage: 1
    };
  }
  const size = Math.min(userPageSize ?? cap, cap);
  // A page picked under a smaller size can be past the end once the columns are known.
  const lastPage = Math.max(1, Math.ceil(known.totalWells / size));
  return { size, currentPage: Math.min(storedPage, lastPage) };
};

/**
 * The monthly view's request and page, shared by the grid and the toolbar export so
 * both read one cached response. Pages follow the Data Table's rule: a filter that fits
 * monthlyMaxPageSize wells loads whole, and a larger one pages, by default at that size.
 */
export default function useMonthlyProduction() {
  const [{ page, pageSize: userPageSize, monthlyShapes }, tableDispatch] = useTable();
  const queryClient = useQueryClient();
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const pdenSource = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting.source
  );

  const syncClientPden = syncWells && syncWells.isSync && syncWells.usePdenFromClient;
  const syncClientSchema = syncWells?.schema;

  const filter: ProductionVolumesFilter = useMemo(
    () => ({
      filterId,
      pdenDataSourceSetting: { source: pdenSource },
      syncClientPden,
      syncClientSchema
    }),
    [filterId, pdenSource, syncClientPden, syncClientSchema]
  );

  // A page picked under another filter can point past the new total, so it is derived
  // during render, before the effect below resets the stored page.
  const filterKey = JSON.stringify(filter);
  const [pageFilterKey, setPageFilterKey] = useState(filterKey);
  useEffect(() => {
    if (pageFilterKey === filterKey) return;
    setPageFilterKey(filterKey);
    changePage(tableDispatch, 1);
  }, [pageFilterKey, filterKey, tableDispatch]);

  // What the filter is known to hold, so the first request is already the right size and
  // the footer survives an error or a page change. Remembered per filter from the last
  // response; before that, the Data view's cached well count says how many wells there are.
  const counts = queryClient.getQueryData<FilterCountT>([
    "filterCounts",
    filterId.id,
    filterId.lastUpdated
  ]);
  const known: Known | undefined =
    monthlyShapes[filterKey] ??
    (counts?.Active === undefined
      ? undefined
      : { totalWells: counts.Active, columnCount: FALLBACK_COLUMN_COUNT });

  const { size, currentPage } = planRequest(
    known,
    userPageSize,
    pageFilterKey === filterKey ? page : 1
  );

  // The footer shows the stored page, so a capped page is written back.
  useEffect(() => {
    if (pageFilterKey === filterKey && currentPage !== page) {
      changePage(tableDispatch, currentPage);
    }
  }, [currentPage, page, pageFilterKey, filterKey, tableDispatch]);

  const isEmptyFilter = !filterId.id || filterId.id === EMPTY_FILTER_ID;
  const query = useProductionVolumesQuery(
    { ...filter, page: currentPage, pageSize: size },
    !isEmptyFilter
  );

  const responseTotal = query.data?.totalWells;
  const responseColumns = query.data?.columns.length;
  // Context state, not a ref: a response that changes the plan, such as a filter that fits
  // once fewer columns are known, must re-render every consumer to send the resized request.
  useEffect(() => {
    if (responseTotal === undefined || responseColumns === undefined) return;
    rememberMonthlyShape(tableDispatch, filterKey, {
      totalWells: responseTotal,
      columnCount: responseColumns
    });
  }, [filterKey, responseTotal, responseColumns, tableDispatch]);

  // What is shown follows the freshest total, which a response may only just have set.
  const latest: Known | undefined =
    responseTotal !== undefined && responseColumns !== undefined
      ? { totalWells: responseTotal, columnCount: responseColumns }
      : known;
  const maxPageSize = monthlyMaxPageSize(latest?.columnCount);

  return {
    filter,
    size,
    maxPageSize,
    pagination: latest !== undefined && latest.totalWells > maxPageSize,
    totalWells: latest?.totalWells,
    isEmptyFilter,
    ...query
  };
}
